import React, { useState, useRef } from 'react';
import { Play, Clock } from 'lucide-react';
import { canRenderPublicVideo } from '../utils/videoUrlValidation';

/**
 * TestettiKVideoPlayer — Thumbnail-first, lazy-load, native HTML5 video for Bunny/CDN URLs.
 * Contract: Only renders when `video.status === 'PUBLISHED'` AND `video.playbackUrl` passes
 * TT-VIDEO-002 whitelist validation. Never uses iframes. Never autoplays. Loads full video
 * only after user clicks Play. Enforces 9:16 aspect ratio.
 */
const TestettiKVideoPlayer = ({ video, title = 'TestettiK Video Kanıtı' }) => {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  if (!canRenderPublicVideo(video)) return null;

  const isVertical = (video.aspectRatio || '9:16') === '9:16';
  const outerStyle = isVertical ? { maxWidth: 420 } : { maxWidth: 720 };
  const aspectStyle = isVertical ? { aspectRatio: '9/16' } : { aspectRatio: '16/9' };

  const handlePlay = () => {
    setPlaying(true);
    // Autofocus play on the native element after mount tick
    setTimeout(() => { videoRef.current?.play?.().catch(() => { }); }, 30);
  };

  return (
    <section className="mx-auto w-full" style={outerStyle} data-testid="testettik-video-player">
      <div
        className="relative rounded-lg overflow-hidden bg-black border shadow-sm"
        style={{ ...aspectStyle, borderColor: 'var(--border-soft)' }}
      >
        {!playing ? (
          <button
            type="button"
            onClick={handlePlay}
            className="absolute inset-0 w-full h-full group cursor-pointer focus-visible:ring-2 focus-visible:ring-white outline-none"
            aria-label={`${title} — Videoyu oynat`}
            data-testid="video-play-button"
          >
            {video.thumbnailUrl ? (
              <img
                src={video.thumbnailUrl}
                alt={title}
                loading="lazy"
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
              />
            ) : (
              <div className="w-full h-full bg-neutral-800" />
            )}
            {/* Play overlay */}
            <div className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/15 transition-colors">
              <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Play className="w-7 h-7 text-neutral-900 ml-1" fill="currentColor" />
              </div>
            </div>
            {video.durationSeconds && (
              <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1">
                <Clock className="w-2.5 h-2.5" />
                {Math.floor(video.durationSeconds / 60)}:{String(Math.round(video.durationSeconds % 60)).padStart(2, '0')}
              </div>
            )}
          </button>
        ) : (
          <video
            ref={videoRef}
            src={video.playbackUrl}
            poster={video.thumbnailUrl || undefined}
            controls
            playsInline
            preload="none"
            className="absolute inset-0 w-full h-full object-contain bg-black"
            data-testid="video-native-element"
          >
            Tarayıcınız bu videoyu oynatmayı desteklemiyor.
          </video>
        )}
      </div>

      {video.publicCaption && (
        <p className="text-sm mt-3 text-center font-medium" style={{ color: 'var(--text-secondary-strong)' }}>
          {video.publicCaption}
        </p>
      )}
    </section>
  );
};

export default TestettiKVideoPlayer;
